import axios, { AxiosInstance, AxiosError } from 'axios';
import { configManager } from '../config/environment';

/**
 * Enterprise-grade API Service for Rate Loader Chat UI
 * Compatible with rate-loader and widget-ui patterns
 */

const DEFAULT_TIMEOUT = 900000; // 15 minutes for long-running operations

/**
 * Get the current API base URL, checking for runtime overrides
 */
function getApiBaseUrl(): string {
  // Check for runtime override from parent component (highest priority)
  const runtimeBaseUrl = typeof window !== 'undefined'
    ? (window as any).__RATE_LOADER_API_BASE_URL__
    : undefined;

  const configUrl = configManager.getApiBaseUrl();
  const fallbackUrl = ''; // No hardcoded fallback — must be configured via props
  const finalUrl = runtimeBaseUrl || configUrl || fallbackUrl;

  return finalUrl;
}

// Create axios instance
const createApiClient = (baseUrl?: string): AxiosInstance => {
  // Priority: explicit baseUrl > runtime window var > config manager > default
  const apiBaseUrl = baseUrl || getApiBaseUrl();

  const instance = axios.create({
    baseURL: apiBaseUrl,
    timeout: DEFAULT_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      config.headers['X-Request-ID'] = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response) {
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

let apiClient = createApiClient();

// Watch for runtime API base URL changes
if (typeof window !== 'undefined') {
  // Expose method to update base URL (matches rate-loader pattern)
  (window as any).__updateRateLoaderApiBaseUrl__ = (newUrl: string) => {
    (window as any).__RATE_LOADER_API_BASE_URL__ = newUrl;
    configManager.updateConfig({ apiBaseUrl: newUrl });
    apiClient.defaults.baseURL = getApiBaseUrl();
  };
}

/**
 * Configure API base URL (for npm package mode)
 */
export const configureApiBaseUrl = (baseUrl: string) => {
  (window as any).__RATE_LOADER_API_BASE_URL__ = baseUrl;
  configManager.updateConfig({ apiBaseUrl: baseUrl });
  apiClient = createApiClient(baseUrl);
};

/**
 * Extract agent name from message (matching UAT logic)
 */
function extractAgentName(msg: any, appName: string): string {
  // Priority 1: Use agentName from metadata
  if (msg.metadata?.agentName) {
    return msg.metadata.agentName;
  }

  // Priority 2: Use requiresAction as fallback
  if (msg.metadata?.requiresAction) {
    const action = msg.metadata.requiresAction;
    if (action.includes('upload')) return 'upload';
  }

  // Priority 3: Infer from message content
  const content = (msg.content || '').toLowerCase();
  if (content.includes('upload')) return 'upload';

  // Priority 4: Default
  const isBot = msg.type === 'assistant' || msg.type === 'system' || msg.bot === true;
  return isBot ? (appName || 'assistant') : 'user';
}

/**
 * Push chat data to backend (matches UAT /api/pushData endpoint)
 */
export const pushChatData = async (
  userId: string,
  sessionId: string,
  appName: string,
  messages: any[],
  jobNumber?: string,
  isCoreFlow?: boolean
): Promise<void> => {
  try {
    // Don't push if no messages
    if (!messages || messages.length === 0) {
      return;
    }

    // Transform messages to match the expected conversationHistory format
    const conversationHistory = messages.map(msg => {
      const isBot = msg.type === 'assistant' || msg.type === 'system' || msg.bot === true;
      const timestamp = msg.timestamp || new Date().toISOString();
      
      // Build message content with API response if available
      let messageContent = msg.content || msg.text || '';
      
      // Append API response if available (like UAT project)
      if (msg.metadata?.apiResponse && msg.metadata?.isApiResponse) {
        try {
          const apiData = typeof msg.metadata.apiResponse === 'string' 
            ? JSON.parse(msg.metadata.apiResponse)
            : msg.metadata.apiResponse;
          
          // Skip large table arrays (snooping/audit data)
          const isLargeResponse = apiData?.table && Array.isArray(apiData.table);
          
          if (!isLargeResponse) {
            messageContent += ` | API Response: ${JSON.stringify(apiData)}`;
          } else {
            // For large responses, only include summary
            const summary = {
              goodResponse: apiData.goodResponse,
              currentStageStatus: apiData.currentStageStatus,
              fileName: apiData.fileName,
              output: apiData.output,
              rowCount: apiData.table?.length || 0,
            };
            messageContent += ` | API Response: ${JSON.stringify(summary)}`;
          }
        } catch {
          // If parsing fails, skip appending
        }
      }
      
      return {
        message: messageContent,
        messageId: jobNumber, // All messages in session use same messageId (jobNumber)
        bot: isBot,
        agentName: extractAgentName(msg, appName),
        userReview: msg.metadata?.reaction || null,
        userComment: msg.metadata?.comment || null,
        messageTimeStamp: timestamp,
      };
    });

    const now = new Date();
    const payload = {
      sessionId,
      endUserData: {
        firstName: '',
        lastName: '',
        middleInitial: null,
        msId: userId,
        email: '',
        managerName: '',
        userRole: 'Rate Loader User',
      },
      conversationHistory,
      loggingData: {
        date: (() => {
          const d = now;
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        })(),
        time: (() => {
          const d = now;
          const hours = String(d.getHours()).padStart(2, '0');
          const minutes = String(d.getMinutes()).padStart(2, '0');
          const seconds = String(d.getSeconds()).padStart(2, '0');
          return `${hours}:${minutes}:${seconds}`;
        })(),
        accessLocation: isCoreFlow ? 'Core' : 'provider-console',
        gdActivatedAgent: null,
        gdEquivalent: null,
      },
      errorData: [],
    };

    await apiClient.post('/api/pushData', payload);
  } catch (error) {
    // Don't throw - push is non-critical
  }
};

/**
 * Fetch chat history (matches UAT /api/conversationHistory endpoint)
 */
export const fetchChatHistory = async (
  userId: string,
  _appName: string = 'RateLoader_Core',
  isCoreFlow?: boolean
): Promise<any[]> => {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);

    const response = await apiClient.get(`/api/conversationHistory/${userId}`, {
      params: {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      },
    });

    const conversations = response.data?.data?.conversations || [];
    
    // Filter out empty conversations (no messages or empty conversationHistory)
    let filtered = conversations.filter((conv: any) => {
      const hasMessages = conv.conversationHistory && 
                         Array.isArray(conv.conversationHistory) && 
                         conv.conversationHistory.length > 0;
      
      return hasMessages;
    });

    // Filter by accessLocation if isCoreFlow is specified
    if (isCoreFlow !== undefined) {
      const targetAccessLocation = isCoreFlow ? 'Core' : 'provider-console';
      filtered = filtered.filter((conv: any) =>
        conv.loggingData?.accessLocation === targetAccessLocation
      );
    }

    // Add updatedAt field based on latest message timestamp
    const enriched = filtered.map((conv: any) => {
      const timestamps = conv.conversationHistory
        .map((msg: any) => msg.messageTimeStamp)
        .filter(Boolean);
      
      const latestTimestamp = timestamps.length > 0
        ? timestamps.reduce((latest: string, current: string) => 
            new Date(current) > new Date(latest) ? current : latest
          )
        : new Date().toISOString();
      
      return {
        ...conv,
        updatedAt: latestTimestamp,
      };
    });

    return enriched;
  } catch (error) {
    return [];
  }
};

/**
 * Validate job number
 */
export const validateJobNumber = async (jobNumber: string): Promise<boolean> => {
  try {
    const response = await apiClient.get(`/api/job/validate/${jobNumber}`);
    return response.data?.valid === true;
  } catch (error) {
    return false;
  }
};

/**
 * Get job status
 */
export const getJobStatus = async (jobNumber: string): Promise<any> => {
  try {
    const response = await apiClient.get(`/api/job/status/${jobNumber}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Submit user feedback (thumbs up/down, comments)
 * POST /api/feedback
 */
export const submitFeedback = async (payload: {
  userId: string;
  sessionId: string;
  messageId: string;
  agentName: string;
  agentResponse: string;
  userFeedback: 'like' | 'dislike' | '';
  userComment?: string;
  createdAt: string;
  accessLocation?: string;
}): Promise<{ status: string; message: string }> => {
  try {
    const response = await apiClient.post('/api/feedback', payload);
    return response.data;
  } catch (error) {
    console.error('[apiService] submitFeedback failed:', error);
    throw error;
  }
};

export const apiService = {
  configureApiBaseUrl,
  pushChatData,
  fetchChatHistory,
  validateJobNumber,
  getJobStatus,
  submitFeedback,
};

export default apiService;
